import {Button, Typography} from "@mui/material";
import {Box, SxProps} from "@mui/system";
import React, {ChangeEvent, FormEvent, useState} from "react";
import {usePasswordResetMutation} from "../app/services/api";
import LoginTextField from "../components/LoginTextField/LoginTextField";
import {SUPPORT_URL} from "../components/ContactText";
import {isValidEmailAddress} from "../utils/emailAddress";

const boxStyle: SxProps = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignContent: 'center',
    flex: 1
};


const PasswordRecovery = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailAddressInQuery = urlParams.get('username');
    const validEmailAddress =  isValidEmailAddress(emailAddressInQuery || '');

    const email = validEmailAddress ? emailAddressInQuery : '';
    const [emailAddress, setEmailAddress] = useState(email || '');
    const [invalidUserInput, setInvalidUserInput] = useState(false);

    let showSuccessMessage = false;
    let showErrorMessage = false;

    const [passwordReset, passwordResetResult] = usePasswordResetMutation();

    if (passwordResetResult.isSuccess) {
        showSuccessMessage = true;
    }

    if (passwordResetResult.isError) {
        showErrorMessage = true;
    }

    const onSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        await passwordReset({ email: emailAddress });
    };

    const submitButtonText = 'Send Email';

    const unknownEmailAddressMessage = () => <>
        <Typography variant="body1" gutterBottom>
            Please enter your email address, click on &lsquo;{submitButtonText}&rsquo;, and we will send account recovery instructions by email.
        </Typography>
    </>;

    const onChangeEmail = (ev: ChangeEvent<HTMLInputElement>, value: string) => {
        setEmailAddress(value);
        setInvalidUserInput(!ev.target.checkValidity());
    };

    const emailAddressBox = <Box>
        {
            unknownEmailAddressMessage()
        }

        <LoginTextField
            id={"email"}
            type="email"
            label={"Email address"}
            placeholder={"user@example.com"}
            required
            value={emailAddress}
            onChangeHandler={(ev: ChangeEvent<HTMLInputElement>, value: string) => onChangeEmail(ev, value)}
        />
    </Box>;

    const messageBox = <Box>
        <Typography variant="body1" gutterBottom>
            Click on &lsquo;{submitButtonText}&rsquo; and we will send account recovery instructions to <strong>{emailAddress}</strong>.
        </Typography>
    </Box>;

    const returnToLoginButton = <Button
        fullWidth
        href="/"
        variant="contained"
        color="secondary"
    >
        Return to Login
    </Button>;

    const content = () => {
        if (showSuccessMessage) {
            return <Box sx={boxStyle}>
                <Box>
                    <Typography variant="h2" gutterBottom>
                        Check your email!
                    </Typography>

                    <Typography gutterBottom>
                        We have sent an email to <strong>{emailAddress}</strong> with further instructions to reset your password. If it doesn&apos;t appear within a few minutes, check your spam folder.
                    </Typography>

                    <Typography gutterBottom>
                        If you have not received any email or if you have any other questions
                        please contact our{" "}
                        <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
                            customer care and support desk
                        </a>.
                    </Typography>
                </Box>
                {returnToLoginButton}
            </Box>;
        }

        if (showErrorMessage) {
            return <Box sx={boxStyle}>
                <Box>
                    <Typography variant="h2" gutterBottom color="error.main">
                        Oops!
                    </Typography>
                    <Typography gutterBottom>
                        Unfortunately, we could not send a recovery email.
                    </Typography>
                    <Typography gutterBottom>
                        Please contact our{" "}
                        <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
                            customer care and support desk{" "}
                        </a> to recover your account.
                    </Typography>
                </Box>
                {returnToLoginButton}
            </Box>;
        }

        return <Box sx={boxStyle}>
            <Box>
                <Typography variant="h2" gutterBottom>
                    Forgotten your Password?
                </Typography>

                <Typography variant="body1" gutterBottom>
                    Don&apos;t worry, it happens to the best of us. Good thing we can help you reset your password.
                </Typography>

                { validEmailAddress ? messageBox : emailAddressBox }
            </Box>
            <Box sx={{ display: 'flex', gap: 1, Button: { flex: 1}, a: {flex: 1}}}>
                <Button
                    id="cancel-button"
                    variant="outlined"
                    color="primary"
                    href={"/"}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    type={"submit"}
                    disabled={passwordResetResult.isLoading || (!Boolean(emailAddress) || invalidUserInput)}
                >
                    {submitButtonText}
                </Button>
            </Box>
        </Box>;
    };

    return (
        <Box component="form" onSubmit={onSubmit} sx={boxStyle}>
            { content()}
        </Box>
    );
};

export default PasswordRecovery;
